import React, { useEffect, useMemo, useState, useRef } from "react";
import { AiOutlinePlusCircle } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { categoriesData as fallbackCategoriesData } from "../../static/data";
import { toast } from "react-toastify";
import { backend_url, server } from "../../server";
import { fetchSiteOptions } from "../../redux/actions/siteOptions";
import { IMAGE_UPLOAD_MAX_BYTES, IMAGE_UPLOAD_MAX_MB } from "../../uploadLimits";
import {
  hasLinkedSocialProfiles,
  SELLER_SOCIAL_REQUIRED_MESSAGE,
} from "../../utils/socialProfileLinks";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const DRAFT_DEBOUNCE_MS = 2000;
const DRAFT_STORAGE_KEY = "createListingDraftId";

const parseCategoryParts = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return { category: "", subcategory: "" };
    const separator = raw.includes(" > ") ? " > " : raw.includes(">") ? ">" : null;
    if (!separator) return { category: raw, subcategory: "" };
    const [main, ...rest] = raw.split(separator);
    return {
        category: (main || "").trim(),
        subcategory: rest.join(separator).trim(),
    };
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const descriptionHasText = (html) =>
    String(html || "")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .trim().length > 0;

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "align",
  "list",
  "bullet",
  "link",
];

const resolveCategoryImageSrc = (value) => {
  if (!value) return "";
  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("data:")
  ) {
    return value;
  }
  const normalized = value.startsWith("/") ? value.slice(1) : value;
  return `${backend_url}${normalized}`;
};

const CreateProduct = () => {
    const { user } = useSelector((state) => state.user);
    const siteOptionsState = useSelector((state) => state.siteOptions);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [images, setImages] = useState([]);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [category, setCategory] = useState("");
    const [subcategory, setSubcategory] = useState("");
    const [categoryLink, setCategoryLink] = useState("");
    const [categoryImage, setCategoryImage] = useState("");
    const [brand, setBrand] = useState("");
    const [color, setColor] = useState("");
    const [externalPartType, setExternalPartType] = useState("");
    const [weaponPlatform, setWeaponPlatform] = useState("");
    const [filterValues, setFilterValues] = useState({});
    const [tags, setTags] = useState("");
    const [originalPrice, setOriginalPrice] = useState();
    const [stock, setStock] = useState(1);
    const [postageFees, setPostageFees] = useState();
    const [dispatchTimeDays, setDispatchTimeDays] = useState(1);
    const [deliveryMethod, setDeliveryMethod] = useState("three_to_five_working_days");
    const [wantFeature, setWantFeature] = useState(false);
    const [featureWeeks, setFeatureWeeks] = useState(1);
    const [draftId, setDraftId] = useState(null);
    const [draftStatus, setDraftStatus] = useState("");
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [restoredDraft, setRestoredDraft] = useState(false);

    const formStateRef = useRef({});
    const imagesRef = useRef([]);
    const draftIdRef = useRef(null);
    const isSavingDraftRef = useRef(false);
    const autoSaveDisabledRef = useRef(false);
    const debounceTimerRef = useRef(null);
    const autoSaveGenerationRef = useRef(0);
    const restoreAttemptedRef = useRef(false);

    const persistDraftId = (id) => {
        draftIdRef.current = id || null;
        setDraftId(id || null);
        try {
            if (id) localStorage.setItem(DRAFT_STORAGE_KEY, id);
            else localStorage.removeItem(DRAFT_STORAGE_KEY);
        } catch {
            // ignore storage errors
        }
    };

    const stopAutoSave = () => {
        autoSaveDisabledRef.current = true;
        autoSaveGenerationRef.current += 1;
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
    };

    const buildDraftPayload = (state = formStateRef.current) => {
        const finalCategory = state.subcategory
            ? `${state.category || ""} > ${state.subcategory}`
            : (state.category || "");
        return {
            name: (state.name && String(state.name).trim()) || "Draft listing",
            description: (state.description && String(state.description).trim()) || "<p></p>",
            category: finalCategory || "Uncategorised",
            brand: state.brand || "",
            color: state.color || "",
            externalPartType: state.externalPartType || "",
            weaponPlatform: state.weaponPlatform || "",
            filterValues: state.filterValues || {},
            tags: state.tags || "",
            originalPrice: Number(state.originalPrice) || 0,
            discountPrice: Number(state.originalPrice) || 0,
            stock: Number(state.stock) || 0,
            postageFees: Number(state.postageFees) || 0,
            dispatchTimeDays: Number(state.dispatchTimeDays) || 1,
            deliveryMethod: state.deliveryMethod || "three_to_five_working_days",
        };
    };

    const applyDraftToForm = (product) => {
        if (!product) return;
        const parts = parseCategoryParts(product.category);
        setName(product.name && product.name !== "Draft listing" ? product.name : "");
        setDescription(product.description && product.description !== "<p></p>" ? product.description : "");
        setCategory(parts.category !== "Uncategorised" ? parts.category : "");
        setSubcategory(parts.subcategory || "");
        setBrand(product.brand || "");
        setColor(product.color || "");
        setExternalPartType(product.externalPartType || "");
        setWeaponPlatform(product.weaponPlatform || "");
        setFilterValues(product.filterValues || {});
        setTags(product.tags || "");
        setOriginalPrice(product.originalPrice || product.discountPrice || undefined);
        setStock(product.stock || 1);
        setPostageFees(product.postageFees || undefined);
        setDispatchTimeDays(product.dispatchTimeDays || 1);
        setDeliveryMethod(product.deliveryMethod || "three_to_five_working_days");
        const existingImages = Array.isArray(product.images)
            ? product.images.map((filename) => ({
                file: null,
                filename,
                preview: `${backend_url}${filename}`,
            }))
            : [];
        setImages(existingImages);
        persistDraftId(product._id);
        setRestoredDraft(true);
        setDraftStatus("Unfinished listing restored.");
    };

    const uploadPendingImages = async (productId) => {
        const pending = (imagesRef.current || []).filter((img) => img?.file);
        if (!productId || pending.length === 0) return;
        const formData = new FormData();
        pending.forEach((img) => formData.append("images", img.file));
        const { data } = await axios.post(
            `${server}/product/add-shop-product-images/${productId}`,
            formData,
            {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
            }
        );
        const uploaded = Array.isArray(data?.images) ? data.images : [];
        if (uploaded.length) {
            setImages((prev) => {
                let index = 0;
                return prev.map((img) => {
                    if (!img?.file) return img;
                    const filename = uploaded[index++];
                    if (!filename) return img;
                    return {
                        file: null,
                        filename,
                        preview: img.preview || `${backend_url}${filename}`,
                    };
                });
            });
        }
    };

    const saveDraftToServer = async ({ withImages = false } = {}) => {
        const payload = buildDraftPayload();
        let id = draftIdRef.current;
        if (id) {
            await axios.put(
                `${server}/product/update-shop-product/${id}`,
                { ...payload, status: "draft" },
                { withCredentials: true }
            );
        } else {
            const res = await axios.post(`${server}/product/create-product-draft`, payload, {
                withCredentials: true,
                headers: { "Content-Type": "application/json" },
            });
            id = res.data?.product?._id || null;
            if (id) persistDraftId(id);
        }
        if (withImages && id) {
            await uploadPendingImages(id);
        }
        return id;
    };

    useEffect(() => {
        formStateRef.current = {
            name,
            description,
            category,
            subcategory,
            brand,
            color,
            externalPartType,
            weaponPlatform,
            filterValues,
            tags,
            originalPrice: originalPrice ?? "",
            stock: stock ?? "",
            postageFees: postageFees ?? "",
            dispatchTimeDays: dispatchTimeDays ?? 1,
            deliveryMethod: deliveryMethod ?? "three_to_five_working_days",
        };
    }, [name, description, category, subcategory, brand, color, externalPartType, weaponPlatform, filterValues, tags, originalPrice, stock, postageFees, dispatchTimeDays, deliveryMethod]);

    useEffect(() => {
        imagesRef.current = images;
    }, [images]);

    useEffect(() => {
        draftIdRef.current = draftId;
    }, [draftId]);

    const hasLinkedSocial = hasLinkedSocialProfiles(user?.linkedSocialProfiles);
    const hasFormContent = !!(name?.trim() || descriptionHasText(description) || category || images.length > 0);

    useEffect(() => {
        if (!user?._id || restoreAttemptedRef.current) return;
        restoreAttemptedRef.current = true;
        let storedId = "";
        try {
            storedId = localStorage.getItem(DRAFT_STORAGE_KEY) || "";
        } catch {
            storedId = "";
        }
        if (!storedId) return;
        axios
            .get(`${server}/product/get-shop-product/${storedId}`, { withCredentials: true })
            .then((res) => {
                const product = res.data?.product;
                if (product && product.status === "draft") {
                    applyDraftToForm(product);
                } else {
                    persistDraftId(null);
                }
            })
            .catch(() => persistDraftId(null));
    }, [user?._id]);

    // Realtime draft: debounced auto-save (no button)
    useEffect(() => {
        if (!hasFormContent || !user?._id || autoSaveDisabledRef.current) return;

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            if (autoSaveDisabledRef.current) return;
            const generation = autoSaveGenerationRef.current;
            const payload = buildDraftPayload();
            setDraftStatus("Saving draft…");
            if (draftIdRef.current) {
                axios.put(
                    `${server}/product/update-shop-product/${draftIdRef.current}`,
                    { ...payload, status: "draft" },
                    { withCredentials: true }
                ).then(() => {
                    if (generation !== autoSaveGenerationRef.current) return;
                    setDraftStatus("Draft saved. Finish it anytime from My Listings.");
                }).catch(() => {
                    if (generation !== autoSaveGenerationRef.current) return;
                    setDraftStatus("Could not auto-save. Use Save as draft.");
                });
            } else {
                isSavingDraftRef.current = true;
                axios.post(`${server}/product/create-product-draft`, payload, {
                    withCredentials: true,
                    headers: { "Content-Type": "application/json" },
                }).then((res) => {
                    if (generation !== autoSaveGenerationRef.current) return;
                    const id = res.data?.product?._id;
                    if (id) persistDraftId(id);
                    setDraftStatus("Draft saved. Finish it anytime from My Listings.");
                }).catch(() => {
                    if (generation !== autoSaveGenerationRef.current) return;
                    setDraftStatus("Could not auto-save. Use Save as draft.");
                }).finally(() => { isSavingDraftRef.current = false; });
            }
        }, DRAFT_DEBOUNCE_MS);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
        };
    }, [hasFormContent, user?._id, draftId, name, description, category, subcategory, brand, color, weaponPlatform, tags, originalPrice, stock, postageFees, dispatchTimeDays, deliveryMethod]);

    // On tab close, save draft if we have content and no draft yet (backup for fast close)
    useEffect(() => {
        if (!hasFormContent) return;
        const onBeforeUnload = () => {
            if (autoSaveDisabledRef.current || isSavingDraftRef.current) return;
            const payload = buildDraftPayload();
            if (draftIdRef.current) {
                fetch(`${server}/product/update-shop-product/${draftIdRef.current}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ ...payload, status: "draft" }),
                    credentials: "include",
                    keepalive: true,
                }).catch(() => {});
                return;
            }
            isSavingDraftRef.current = true;
            fetch(`${server}/product/create-product-draft`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
                credentials: "include",
                keepalive: true,
            }).catch(() => {});
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [hasFormContent, draftId]);

    // Persist the listing as a draft if the seller leaves this page (e.g. to link social).
    useEffect(() => {
        return () => {
            if (autoSaveDisabledRef.current || isSavingDraftRef.current) return;
            const state = formStateRef.current || {};
            const descText = descriptionHasText(state.description);
            const hasContent = !!(
                String(state.name || "").trim() ||
                descText ||
                state.category ||
                (imagesRef.current || []).length > 0
            );
            if (!hasContent) return;
            const payload = { ...buildDraftPayload(state), status: "draft" };
            const headers = { "Content-Type": "application/json" };
            if (draftIdRef.current) {
                fetch(`${server}/product/update-shop-product/${draftIdRef.current}`, {
                    method: "PUT",
                    headers,
                    body: JSON.stringify(payload),
                    credentials: "include",
                    keepalive: true,
                }).catch(() => {});
                return;
            }
            fetch(`${server}/product/create-product-draft`, {
                method: "POST",
                headers,
                body: JSON.stringify(payload),
                credentials: "include",
                keepalive: true,
            }).catch(() => {});
        };
    }, []);

    const featureTiers = useMemo(() => {
        const fp = siteOptionsState?.options?.global?.featuredProductSettings || {};
        const tiers = Array.isArray(fp.pricingTiers) ? fp.pricingTiers : [];
        return tiers.filter((t) => t && t.isActive !== false);
    }, [siteOptionsState?.options?.global?.featuredProductSettings]);

    const handleImageChange = (e) => {
        e.preventDefault();

        const inputFiles = Array.from(e.target.files || []);
        
        if (!inputFiles.length) return;

        // Validate file sizes
        const oversizedFiles = inputFiles.filter(
            (file) => file.size > IMAGE_UPLOAD_MAX_BYTES
        );
        if (oversizedFiles.length > 0) {
            toast.error(
                `One or more images exceed the ${IMAGE_UPLOAD_MAX_MB}MB size limit. Maximum upload size is ${IMAGE_UPLOAD_MAX_MB}MB per image.`
            );
            e.target.value = ""; // Reset input
            return;
        }

        const files = inputFiles.map((file) => ({
            file,
            preview: URL.createObjectURL(file),
        }));

        setImages((prevImages) => [...prevImages, ...files]);
    };

    // Cleanup image preview URLs on unmount or when images change
    // Use ref to track previous images to avoid revoking URLs that are still in use
    const prevImagesRef = useRef([]);
    
    useEffect(() => {
        // Revoke URLs for images that are no longer in the array
        const currentPreviews = new Set(images.map(img => img?.preview).filter(Boolean));
        prevImagesRef.current.forEach((prevImage) => {
            if (prevImage?.preview && !currentPreviews.has(prevImage.preview)) {
                URL.revokeObjectURL(prevImage.preview);
            }
        });
        prevImagesRef.current = images;
        
        // Cleanup on unmount
        return () => {
            images.forEach((image) => {
                if (image?.preview) {
                    URL.revokeObjectURL(image.preview);
                }
            });
        };
    }, [images]);

    const handleRemoveImage = (index) => {
        setImages((prev) => {
            const next = [...prev];
            const removed = next.splice(index, 1)[0];
            if (removed?.preview && String(removed.preview).startsWith("blob:")) {
                URL.revokeObjectURL(removed.preview);
            }
            if (removed?.filename && draftIdRef.current) {
                axios.delete(
                    `${server}/product/delete-shop-product-image/${draftIdRef.current}`,
                    { params: { filename: removed.filename }, withCredentials: true }
                ).catch(() => {});
            }
            return next;
        });
    };

    const catalogCategoriesRaw =
        siteOptionsState?.options?.global?.catalog?.categories || [];
    const hasSiteOptions = Boolean(siteOptionsState?.options?.global);
    const isSiteOptionsLoading = Boolean(siteOptionsState?.loading?.global);

    const availableCategories = useMemo(() => {
        if (Array.isArray(catalogCategoriesRaw) && catalogCategoriesRaw.length) {
            const normalized = catalogCategoriesRaw
                .filter((category) => category && category.name && category.isActive !== false)
                .sort(
                    (a, b) => (typeof a.order === "number" ? a.order : 0) - (typeof b.order === "number" ? b.order : 0)
                )
                .map((category) => {
                    const name = category.name?.trim() || "";
                    const slugSource = category.slug?.trim() || name;
                    const fallbackLink = slugSource
                        ? `/products?category=${encodeURIComponent(slugSource)}`
                        : "/products";

                    return {
                        name,
                        slug: slugSource,
                        link: category.link?.trim() || fallbackLink,
                        image: category.image || category.icon || "",
                        subcategories: (category.subcategories || [])
                            .filter((sub) => sub && sub.name && sub.isActive !== false)
                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                            .map((sub) => ({
                                name: sub.name?.trim() || "",
                                slug: sub.slug?.trim() || "",
                            })),
                    };
                });

            if (normalized.length) {
                return normalized;
            }
        }

        return (fallbackCategoriesData || []).map((item) => ({
            name: item.title,
            link:
                item.link || `/products?category=${encodeURIComponent(item.title || "")}`,
            image: item.image_Url || "",
            subcategories: [],
        }));
    }, [catalogCategoriesRaw]);

    const selectedCategoryData = useMemo(() => {
        return availableCategories.find((cat) => cat.name === category);
    }, [availableCategories, category]);

    const availableSubcategories = useMemo(() => {
        return selectedCategoryData?.subcategories || [];
    }, [selectedCategoryData]);

    const productFilters = siteOptionsState?.options?.global?.productFilters || {};
    const filterGroups = useMemo(() => {
        const fromGroups = Array.isArray(productFilters?.groups) ? productFilters.groups : [];
        if (fromGroups.length > 0) {
            return fromGroups
                .filter((group) => group && group.isActive !== false && group.key && group.label)
                .map((group, groupIndex) => ({
                    key: String(group.key || "").trim(),
                    label: String(group.label || "").trim(),
                    options: Array.isArray(group.options)
                        ? group.options.filter((item) => item && item.name).sort((a, b) => (a.order || 0) - (b.order || 0))
                        : [],
                    visibleCategories: Array.isArray(group.visibleCategories) ? group.visibleCategories : [],
                    order: typeof group.order === "number" ? group.order : groupIndex,
                }))
                .sort((a, b) => (a.order || 0) - (b.order || 0));
        }
        return [
            { key: "brands", label: "Brand", options: productFilters.brands || [], visibleCategories: productFilters?.visibility?.brands || [], order: 0 },
            { key: "colors", label: "Primary colour", options: productFilters.colors || [], visibleCategories: productFilters?.visibility?.colors || [], order: 1 },
            { key: "externalPartTypes", label: "External part type", options: productFilters.externalPartTypes || [], visibleCategories: productFilters?.visibility?.externalPartTypes || [], order: 2 },
            { key: "weaponPlatforms", label: "Weapon platform", options: productFilters.weaponPlatforms || [], visibleCategories: productFilters?.visibility?.weaponPlatforms || [], order: 3 },
        ].map((group) => ({
            ...group,
            options: (group.options || []).filter((item) => item && item.name).sort((a, b) => (a.order || 0) - (b.order || 0)),
        }));
    }, [productFilters]);

    const selectedCategorySlug = useMemo(() => {
        if (!category) return "";
        const raw = Array.isArray(catalogCategoriesRaw) ? catalogCategoriesRaw : [];
        const match = raw.find((c) => c && c.name === category);
        const slugValue = match?.slug || match?.name || category;
        return slugValue || "";
    }, [catalogCategoriesRaw, category]);

    const isFilterGroupVisible = (group) => {
        // Do not show any of these dropdowns until a category is selected
        if (!category) return false;
        const list = group.visibleCategories;

        // If no categories are configured for this filter group,
        // show it for all selected categories
        if (!Array.isArray(list) || list.length === 0) return true;

        if (!selectedCategorySlug) return false;

        // Otherwise only show when current category slug is explicitly enabled
        return list.includes(selectedCategorySlug);
    };

    const visibleFilterGroups = useMemo(
        () => filterGroups.filter((group) => isFilterGroupVisible(group)),
        [filterGroups, category, selectedCategorySlug]
    );

    useEffect(() => {
        if (!hasSiteOptions && !isSiteOptionsLoading) {
            dispatch(fetchSiteOptions());
        }
    }, [dispatch, hasSiteOptions, isSiteOptionsLoading]);

    const keepDraftOnValidationError = async (message) => {
        toast.error(message);
        try {
            await saveDraftToServer({ withImages: false });
            setDraftStatus("Draft kept. Fix the issue and continue when you are ready.");
        } catch {
            setDraftStatus("Could not save draft. Use Save as draft.");
        }
    };

    const handleSaveDraft = async () => {
        if (!hasFormContent) {
            toast.error("Add a name, description, category, or image before saving a draft.");
            return;
        }
        setIsSavingDraft(true);
        try {
            const id = await saveDraftToServer({ withImages: true });
            if (id) {
                toast.success("Draft saved. You can finish it from My Listings.");
                setDraftStatus("Draft saved. Finish it anytime from My Listings.");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to save draft");
        } finally {
            setIsSavingDraft(false);
        }
    };

    const handleStartNewListing = () => {
        persistDraftId(null);
        setRestoredDraft(false);
        setName("");
        setDescription("");
        setCategory("");
        setSubcategory("");
        setCategoryLink("");
        setCategoryImage("");
        setBrand("");
        setColor("");
        setExternalPartType("");
        setWeaponPlatform("");
        setFilterValues({});
        setTags("");
        setOriginalPrice(undefined);
        setStock(1);
        setPostageFees(undefined);
        setImages([]);
        setDraftStatus("");
        toast.info("Started a new listing. Your previous draft remains in My Listings.");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!category) {
            await keepDraftOnValidationError("Please choose a category.");
            return;
        }
        if (availableSubcategories.length === 0) {
            await keepDraftOnValidationError("This category has no subcategories configured. Please choose a different category or ask admin to add subcategories.");
            return;
        }
        if (!subcategory) {
            await keepDraftOnValidationError("Please choose a subcategory.");
            return;
        }
        const finalCategory = subcategory ? `${category} > ${subcategory}` : category;
        const payload = {
            name,
            description,
            category: finalCategory,
            brand: brand || "",
            color: color || "",
            externalPartType: externalPartType || "",
            weaponPlatform: weaponPlatform || "",
            filterValues,
            tags,
            originalPrice: originalPrice ?? 0,
            discountPrice: originalPrice ?? 0,
            stock: stock ?? 0,
            postageFees: postageFees ?? 0,
            dispatchTimeDays: dispatchTimeDays ?? 1,
            deliveryMethod: deliveryMethod ?? "three_to_five_working_days",
            status: "active",
        };
        try {
            const id = await saveDraftToServer({ withImages: true });
            if (!id) {
                toast.error("Could not save listing. Please try again.");
                return;
            }
            if (!hasLinkedSocial) {
                setDraftStatus("Draft saved. Link Facebook or Instagram on Overview to publish this listing.");
                toast.info(
                    `${SELLER_SOCIAL_REQUIRED_MESSAGE} Your listing was saved as a draft.`
                );
                return;
            }
            stopAutoSave();
            const { data } = await axios.put(
                `${server}/product/update-shop-product/${id}`,
                { ...payload, status: "active" },
                { withCredentials: true }
            );
            if (data?.code === "SOCIAL_REQUIRED" || data?.published === false) {
                autoSaveDisabledRef.current = false;
                setDraftStatus("Draft saved. Link Facebook or Instagram on Overview to publish this listing.");
                toast.info(data?.message || `${SELLER_SOCIAL_REQUIRED_MESSAGE} Your listing was saved as a draft.`);
                return;
            }
            persistDraftId(null);
            toast.success("Product created successfully!");
            if (wantFeature && featureWeeks) {
                navigate(`/profile/feature-product?productId=${id}&weeks=${featureWeeks}`, { replace: true });
            } else {
                setTimeout(() => navigate("/profile/products", { replace: true }), 300);
            }
        } catch (err) {
            autoSaveDisabledRef.current = false;
            try {
                await saveDraftToServer({ withImages: true });
            } catch {
                // keep the original publish error below
            }
            toast.error(err?.response?.data?.message || "Failed to publish product");
            setDraftStatus("Listing kept as a draft. You can try again without starting over.");
        }
    };

    return (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 lg:p-6">
            <header className="mb-6">
                <h2 className="text-lg font-semibold text-slate-900">Sell Items</h2>
                <p className="text-sm text-slate-500">
                    Create a new product listing to start selling on the platform. Unfinished listings are saved as drafts automatically.
                </p>
                {draftStatus ? (
                    <p className="mt-2 text-xs font-medium text-[#38513b]">{draftStatus}</p>
                ) : null}
                {!hasLinkedSocial ? (
                    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                        <p>
                            You can save this listing as a draft without a social profile. To publish it live,{" "}
                            <Link to="/profile/overview" className="font-semibold underline hover:text-amber-900">
                                link Facebook or Instagram on Overview
                            </Link>
                            .
                        </p>
                    </div>
                ) : null}
                {restoredDraft ? (
                    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                        <p>You have an unfinished listing. Continue below, or start a new one — this draft will stay in My Listings.</p>
                        <button
                            type="button"
                            onClick={handleStartNewListing}
                            className="mt-2 text-xs font-semibold text-[#38513b] underline hover:text-[#2f4232]"
                        >
                            Start a new listing
                        </button>
                    </div>
                ) : null}
            </header>
            <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                    <label className="pb-2 block font-medium text-sm text-gray-700">
                        Upload Images <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-2">Maximum upload size: {IMAGE_UPLOAD_MAX_MB}MB per image</p>
                    <input
                        type="file"
                        name=""
                        id="upload"
                        className="hidden"
                        multiple
                        accept="image/*"
                        onChange={handleImageChange}
                    />
                    <div className="w-full flex items-center flex-wrap gap-3">
                        <label htmlFor="upload" className="cursor-pointer">
                            <span className="flex items-center justify-center w-[120px] h-[120px] border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-[#38513b] hover:text-[#38513b] transition">
                                <AiOutlinePlusCircle size={28} />
                            </span>
                        </label>
                        {images &&
                            images.map((image, index) => {
                                const isFirstImage = index === 0;
                                return (
                                <div
                                    key={image.preview}
                                        draggable
                                        onDragStart={(e) => {
                                            e.dataTransfer.setData('text/plain', index.toString());
                                            e.currentTarget.style.opacity = '0.5';
                                        }}
                                        onDragEnd={(e) => {
                                            e.currentTarget.style.opacity = '1';
                                        }}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            e.currentTarget.style.border = '2px dashed #38513b';
                                        }}
                                        onDragLeave={(e) => {
                                            e.currentTarget.style.border = '';
                                        }}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            e.currentTarget.style.border = '';
                                            const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
                                            const newImages = [...images];
                                            const [removed] = newImages.splice(draggedIndex, 1);
                                            newImages.splice(index, 0, removed);
                                            setImages(newImages);
                                        }}
                                        className={`relative h-[120px] w-[120px] rounded-lg overflow-hidden shadow-sm border border-gray-200 cursor-move ${isFirstImage ? 'ring-2 ring-[#38513b] ring-offset-2' : ''}`}
                                >
                                    <img
                                        src={image.preview}
                                        alt={`Product ${index + 1}`}
                                        className="h-full w-full object-contain p-3"
                                    />
                                    <button
                                        type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveImage(index);
                                            }}
                                        className="absolute top-1 right-1 bg-black/70 hover:bg-black text-white text-xs px-2 py-1 rounded"
                                    >
                                        Remove
                                    </button>
                                        {isFirstImage && (
                                            <span className="absolute bottom-1 left-1 bg-[#38513b] text-white text-xs px-2 py-1 rounded-md">Featured</span>
                                        )}
                                </div>
                                );
                            })}
                    </div>
                    {images.length === 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                            Click the box above to upload product images. You can upload multiple images.
                        </p>
                    )}
                    {images.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                            Drag images to reorder them. The first image will be the featured image.
                        </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                        Recommended size: 800x800px or 1000x1000px (square images work best)
                    </p>
                </div>

                <div>
                    <label className="pb-2 block font-medium text-sm text-gray-700">
                        Product Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="name"
                        value={name}
                        className="appearance-none block w-full px-3 h-[40px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter your product name..."
                        required
                    />
                </div>

                <div>
                    <label className="pb-2 block font-medium text-sm text-gray-700">
                        Description <span className="text-red-500">*</span>
                    </label>
                    <ReactQuill
                        theme="snow"
                        value={description}
                        onChange={setDescription}
                        modules={quillModules}
                        formats={quillFormats}
                        className="bg-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Use the toolbar to format product details. Rich text will be saved as HTML.
                    </p>
                </div>

                <div className="grid gap-4 lg:grid-cols-1">
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <label className="pb-2 block font-medium text-sm text-gray-700">
                                Category <span className="text-red-500">*</span>
                            </label>
                            <select
                                className="w-full appearance-none block px-3 h-[40px] border border-gray-300 rounded-[3px] focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                value={category}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    const selected = availableCategories.find((item) => item.name === value);
                                    setCategory(value);
                                    setSubcategory("");
                                    setCategoryLink(selected?.link || "");
                                    setCategoryImage(selected?.image || "");
                                }}
                                required
                            >
                                <option value="">Choose a category</option>
                                {availableCategories.map((item) => (
                                    <option key={item.name} value={item.name}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="pb-2 block font-medium text-sm text-gray-700">
                                Subcategory <span className="text-red-500">*</span>
                            </label>
                            <select
                                className="w-full appearance-none block px-3 h-[40px] border border-gray-300 rounded-[3px] focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-500"
                                value={subcategory}
                                onChange={(e) => setSubcategory(e.target.value)}
                                disabled={!category || availableSubcategories.length === 0}
                                required
                            >
                                <option value="">
                                    {availableSubcategories.length === 0 ? "No subcategories available" : "Choose subcategory"}
                                </option>
                                {availableSubcategories
                                    .slice()
                                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                                    .map((sub) => (
                                        <option key={sub.name} value={sub.name}>
                                            {sub.name}
                                        </option>
                                    ))}
                            </select>
                        </div>
                    </div>
                    {!availableCategories.length && (
                            <p className="mt-1 text-xs text-red-500">
                                No categories available. Contact the admin to configure header categories.
                            </p>
                        )}
                        {categoryImage && (
                            <div className="flex items-center gap-3 mt-2">
                                <img
                                    src={resolveCategoryImageSrc(categoryImage)}
                                    alt="Category"
                                    className="h-12 w-12 object-contain rounded border"
                                />
                                {categoryLink && (
                                    <a
                                        href={categoryLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-blue-600 underline"
                                    >
                                        View category link
                                    </a>
                                )}
                            </div>
                        )}

                    <div className="grid gap-4 sm:grid-cols-3">
                        {visibleFilterGroups.map((group) => (
                            <div key={group.key}>
                                <label className="pb-2 block font-medium text-sm text-gray-700">
                                    {group.label} <span className="text-gray-400">(optional)</span>
                                </label>
                                <select
                                    className="w-full appearance-none block px-3 h-[40px] border border-gray-300 rounded-[3px] focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    value={filterValues[group.key] || ""}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setFilterValues((prev) => ({ ...prev, [group.key]: value }));
                                        if (group.key === "brands") setBrand(value);
                                        if (group.key === "colors") setColor(value);
                                        if (group.key === "weaponPlatforms") setWeaponPlatform(value);
                                        if (group.key === "externalPartTypes") setExternalPartType(value);
                                    }}
                                >
                                    <option value="">Choose {group.label.toLowerCase()}</option>
                                    {(group.options || []).map((item) => (
                                        <option key={`${group.key}-${item.name}`} value={item.name}>
                                            {item.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ))}
                    </div>

                    <div>
                        <label className="pb-2 block font-medium text-sm text-gray-700">
                            Tags <span className="text-gray-400">(optional)</span>
                        </label>
                        <input
                            type="text"
                            name="tags"
                            value={tags}
                            className="appearance-none block w-full px-3 h-[40px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            onChange={(e) => setTags(e.target.value)}
                            placeholder="Enter product tags (comma separated)..."
                        />
                    </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-3">
                    <div>
                        <label className="pb-2 block font-medium text-sm text-gray-700">
                            Price <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            inputMode="decimal"
                            name="price"
                            value={originalPrice || ""}
                            className="appearance-none block w-full px-3 h-[40px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            onChange={(e) => setOriginalPrice(e.target.value)}
                            placeholder="0.00"
                            required
                        />
                    </div>

                    <div>
                        <label className="pb-2 block font-medium text-sm text-gray-700">
                            Postage Fees <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            inputMode="decimal"
                            name="postageFees"
                            value={postageFees || ""}
                            className="appearance-none block w-full px-3 h-[40px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            onChange={(e) => setPostageFees(e.target.value)}
                            placeholder="0.00"
                            required
                        />
                    </div>

                    <div>
                        <label className="pb-2 block font-medium text-sm text-gray-700">
                            Dispatch time (days)
                        </label>
                        <select
                            value={dispatchTimeDays}
                            onChange={(e) => setDispatchTimeDays(Number(e.target.value))}
                            className="appearance-none block w-full px-3 h-[40px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                            {[1, 2, 3, 4, 5].map((d) => (
                                <option key={d} value={d}>
                                    {d} day{d === 1 ? "" : "s"}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="pb-2 block font-medium text-sm text-gray-700">
                            Delivery method
                        </label>
                        <select
                            value={deliveryMethod}
                            onChange={(e) => setDeliveryMethod(e.target.value)}
                            className="appearance-none block w-full px-3 h-[40px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                            <option value="next_day">Next day</option>
                            <option value="two_working_days">2 Working Days</option>
                            <option value="three_to_five_working_days">3-5 business working days</option>
                        </select>
                    </div>

                    <div>
                        <label className="pb-2 block font-medium text-sm text-gray-700">
                            Stock <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            inputMode="numeric"
                            name="stock"
                            value={stock || ""}
                            className="appearance-none block w-full px-3 h-[40px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            onChange={(e) => setStock(e.target.value)}
                            placeholder="0"
                            required
                        />
                    </div>
                </div>

                {featureTiers.length > 0 && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                        <label className="flex items-center gap-2 mb-3">
                            <input
                                type="checkbox"
                                checked={wantFeature}
                                onChange={(e) => setWantFeature(e.target.checked)}
                            />
                            <span className="text-sm font-medium text-slate-700">Feature this product on the homepage</span>
                        </label>
                        {wantFeature && (
                            <div>
                                <label className="block text-sm text-slate-600 mb-2">Duration</label>
                                <select
                                    value={featureWeeks}
                                    onChange={(e) => setFeatureWeeks(Number(e.target.value))}
                                    className="w-full max-w-xs border border-slate-200 rounded-[3px] px-3 h-[40px] text-sm"
                                >
                                    {featureTiers.map((t) => (
                                        <option key={t.weeks} value={t.weeks}>
                                            {t.label || `${t.weeks} week${t.weeks > 1 ? "s" : ""}`} — {Number(t.price) > 0 ? `£${Number(t.price).toFixed(2)}` : "Free"}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-slate-500 mt-2">
                                    After you create the listing, you will be taken to the feature page. Free options are applied immediately; paid options are charged until the period ends.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                    <button
                        type="button"
                        onClick={handleSaveDraft}
                        disabled={isSavingDraft}
                        className="cursor-pointer text-center block w-full px-3 h-[45px] border border-slate-300 rounded-[3px] bg-white text-slate-800 font-semibold hover:bg-slate-50 transition disabled:opacity-60"
                    >
                        {isSavingDraft ? "Saving draft…" : "Save as draft"}
                    </button>
                    <input
                        type="submit"
                        value="Sell Item"
                        className="cursor-pointer text-center block w-full px-3 h-[45px] border border-gray-300 rounded-[3px] bg-[#38513B] text-white font-semibold hover:opacity-90 transition"
                    />
                </div>
            </form>
        </div>
    );
};

export default CreateProduct;